import React, { useCallback, useState } from 'react';
import { cn } from '../../utils/cn';
import { FolderNode } from '../../../core/types/asset.types';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { Folder } from 'lucide-react';

interface FolderGridItemProps {
  folder: FolderNode;
  size: 'small' | 'medium' | 'large';
}

export const FolderGridItem: React.FC<FolderGridItemProps> = ({ folder, size }) => {
  const { selectFolder, toggleFolderExpanded, draggedAssetId, moveAsset } = useProjectPanelStore();
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDoubleClick = useCallback(() => {
    selectFolder(folder.id);
    if (!folder.expanded) {
      toggleFolderExpanded(folder.id);
    }
  }, [folder.id, folder.expanded, selectFolder, toggleFolderExpanded]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    // Only accept internal asset drags, not external file drops
    if (draggedAssetId) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setIsDragOver(true);
    }
  }, [draggedAssetId]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    // Don't stopPropagation — let the grid's handleDrop also fire to clear isDragOver
    setIsDragOver(false);

    if (draggedAssetId) {
      moveAsset(draggedAssetId, folder.id);
    }
  }, [draggedAssetId, folder.id, moveAsset]);

  const sizeClass = { small: 'w-20 h-20', medium: 'w-24 h-24', large: 'w-32 h-32' }[size];
  const iconSize = { small: 28, medium: 36, large: 48 }[size];

  return (
    <div
      onDoubleClick={handleDoubleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'flex flex-col items-center p-2 rounded cursor-pointer transition-all',
        'hover:bg-zinc-700/50',
        isDragOver && 'bg-blue-600/30 ring-2 ring-blue-500 ring-dashed'
      )}
    >
      <div className={cn(
        'flex items-center justify-center rounded mb-1',
        sizeClass,
      )}>
        <Folder size={iconSize} className={cn('text-yellow-500/80', isDragOver && 'text-blue-400')} />
      </div>
      <div className="w-full text-center text-xs truncate px-1 text-zinc-300">
        {folder.name}
      </div>
    </div>
  );
};
